import axios from 'axios'

import { COP_COMMON_ERROR_MESSAGES } from '../constants/common-error-messages'
import { COP_API_BASE_URL, SUBSCRIPTIONS_ENDPOINT, SUBSCRIPTIONS_LIST_ENDPOINT } from '../constants/service-endpoints'
import { ToastService } from './toast.service'
import { buildCopHeaders, getSilentToken } from './tokenUtils'

export interface SubscriptionListParams {
  top?: number
  skip?: number
  orderby?: string
  count?: boolean
  filter?: string
}

export interface Subscription {
  customerId: string
  id: string
  subscriptionNumber: string
  customerName: string
  validFrom: string
  validTo: string
  status: 'active' | 'warning' | 'inactive'
}

export interface SubscriptionListResponse {
  '@odata.count'?: number
  value: Subscription[]
}

const SUBSCRIPTION_TYPES = ['Bronze', 'Gold', 'Silver']

function buildFilterExpression(search: string): string {
  const escaped = search.replace(/'/g, "''")
  const matchedType = SUBSCRIPTION_TYPES.find((type) =>
    type.toLowerCase().startsWith(escaped.toLowerCase()),
  )
  const typeFilter = matchedType ? ` or (type eq '${matchedType}')` : ''
  return (
    `((contains(customerId,'${escaped}'))` +
    ` or (contains(customerName,'${escaped}'))` +
    ` or (contains(country,'${escaped}'))` +
    typeFilter +
    ` or (customerEmailAddresses/any(s: contains(s, '${escaped}')))` +
    ` or (siteIds/any(s: contains(s, '${escaped}'))))`
  )
}

function buildODataQuery(params: Required<SubscriptionListParams>): string {
  const query = new URLSearchParams()
  query.set('$orderby', params.orderby)
  query.set('$count', String(params.count))
  query.set('$top', String(params.top))
  query.set('$skip', String(params.skip))
  if (params.filter) {
    query.set('$filter', buildFilterExpression(params.filter))
  }
  return `?${query.toString()}`
}

export async function fetchSubscriptions(
  params: SubscriptionListParams = {},
): Promise<SubscriptionListResponse | null> {
  const {
    top = 10,
    skip = 0,
    orderby = 'customerName asc',
    count = true,
    filter = '',
  } = params

  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${SUBSCRIPTIONS_LIST_ENDPOINT}${buildODataQuery({ top, skip, orderby, count, filter })}`
    const headers = buildCopHeaders(token)

    const response = await axios.get<SubscriptionListResponse>(url, { headers })
    const { '@odata.count': odataCount, value } = response.data
    return { '@odata.count': odataCount, value }
  } catch (error) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['subscriptions-loading'],
    )
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

export async function deleteSubscription(id: string): Promise<boolean> {
  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${SUBSCRIPTIONS_ENDPOINT}/${id}`
    const headers = buildCopHeaders(token)

    await axios.delete(url, { headers })
    return true
  } catch (error) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['subscription-deletion'],
    )
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return false
  }
}

